import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import AvatarIcon from '../../components/atoms/AvatarIcon';
import Button from '../../components/atoms/Button';
import Icon, { IconSizes } from '../../components/foundations/Icon';
import Popover from '../../components/molecules/Popover';
import SearchTextField from '../../components/molecules/SearchTextField/SearchTextField';
import ConnectionRow from './ConnectionRow';
import {
    CONNECTION_TYPE_ORDER,
    SEARCH_MIN_ITEMS,
    TRANSLATION_KEY,
    TRIGGER_MAX_LOGOS,
    groupLabelKey,
} from '../constants';
import { connectionItemShape } from '../propShapes';
import css from './ConnectionsMenu.css';

const MENU_ANCHOR_ORIGIN = { vertical: 'bottom', horizontal: 'right' };
const MENU_TRANSFORM_ORIGIN = { vertical: 'top', horizontal: 'right' };

const connectedFirst = items => [...items].sort((first, second) => Number(second.connected) - Number(first.connected));

const ConnectionsMenu = ({ items, loadError, onRetry }) => {
    const triggerRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    if (!items.length && !loadError) return null;

    const sortedItems = connectedFirst(items);
    const showSearch = items.length >= SEARCH_MIN_ITEMS;
    const normalizedQuery = query.trim().toLowerCase();
    const visibleItems =
        showSearch && normalizedQuery
            ? sortedItems.filter(item => item.name.toLowerCase().includes(normalizedQuery))
            : sortedItems;
    const groups = CONNECTION_TYPE_ORDER.map(type => ({
        type,
        items: visibleItems.filter(item => item.type === type),
    })).filter(group => group.items.length);

    return (
        <>
            <span ref={triggerRef} className={css.triggerAnchor}>
                <Button
                    type="flat"
                    className={css.trigger}
                    ariaExpanded={open}
                    onClick={() => setOpen(isOpen => !isOpen)}
                >
                    <span className={css.triggerContent}>
                        <span className={css.logoStack}>
                            {sortedItems.slice(0, TRIGGER_MAX_LOGOS).map(({ id, Logo }) => (
                                <AvatarIcon key={id} className={css.stackTile}>
                                    <Logo className={css.stackLogo} />
                                </AvatarIcon>
                            ))}
                        </span>
                        <Translate id={`${TRANSLATION_KEY}.TRIGGER`} />
                        <Icon
                            name="chevron-down"
                            size={IconSizes.X_SMALL}
                            interactive={false}
                            className={css.chevron}
                        />
                    </span>
                </Button>
            </span>
            <Popover
                showPopover={open}
                anchorEl={triggerRef.current}
                anchorOrigin={MENU_ANCHOR_ORIGIN}
                transformOrigin={MENU_TRANSFORM_ORIGIN}
                PaperProps={{ className: css.panel }}
                onClose={() => setOpen(false)}
            >
                <div className={css.header}>
                    <span className={css.title}>
                        <Translate id={`${TRANSLATION_KEY}.TITLE`} />
                    </span>
                    <span className={css.description}>
                        <Translate id={`${TRANSLATION_KEY}.DESCRIPTION`} />
                    </span>
                </div>
                <div className={css.body}>
                    {loadError && (
                        <div className={css.loadError}>
                            <Translate id={`${TRANSLATION_KEY}.LOAD_ERROR`} />
                            <Button type="link" onClick={onRetry}>
                                <Translate id={`${TRANSLATION_KEY}.RETRY`} />
                            </Button>
                        </div>
                    )}
                    {showSearch && (
                        <SearchTextField
                            search={query}
                            onSearchChange={setQuery}
                            placeholder={`${TRANSLATION_KEY}.SEARCH_PLACEHOLDER`}
                            iconSize={IconSizes.X_SMALL}
                            className={css.search}
                        />
                    )}
                    {groups.map(group => (
                        <div key={group.type}>
                            <div className={css.groupLabel}>
                                <Translate id={groupLabelKey(group.type)} />
                            </div>
                            {group.items.map(item => (
                                <ConnectionRow key={item.id} item={item} />
                            ))}
                        </div>
                    ))}
                    {!groups.length && !loadError && (
                        <div className={css.noResults}>
                            <Translate id={`${TRANSLATION_KEY}.NO_RESULTS`} />
                        </div>
                    )}
                </div>
            </Popover>
        </>
    );
};

ConnectionsMenu.propTypes = {
    items: PropTypes.arrayOf(connectionItemShape).isRequired,
    loadError: PropTypes.bool.isRequired,
    onRetry: PropTypes.func.isRequired,
};

export default ConnectionsMenu;
